<script>
import { depositRequest } from "../../../tables"
import { serviceColors, statusColors } from "../../../config/deposit-request"
import pagination from "../../../config/pagination"

export default {
  props: {
    path: {
      type: String,
      required: false,
      default: null,
    },
    viewOnly: {
      type: Boolean,
      default: false
    },
    excludes: {
      type: Array,
      default: () => []
    },
    filters: {
      type: Object,
      default: () => ({
        page: 1,
        q: '',
        types: [],
        statuses: [],
        clients: [],
        date: [],
        amount: null
      })
    }
  },
  computed: {
    serviceColors,
    statusColors,
    pagination() {
      return { pageSize: this.pageSize, current: this.items.current_page, total: this.items.total, showSizeChanger: true }
    },
    pageSize() {
      return this.items.per_page
    },
    columns() {
      if (this.viewOnly)
        this.excludes.push('action')

      return depositRequest.filter((o) => ! this.excludes.includes(o.key))
    },
    baseUri() {
      return this.path ?? this.routeUri('hyperion.finances.deposit')
    },
    exportUri() {
      let params = _.merge(this.filters, {
        timestamp: (new Date).getTime()
      })

      return this.routeUri('hyperion.finances.deposit.export') + '?' + this.$qs.stringify(params)
    }
  },
  data() {
    return {
      filtering: false,
      visible: false,
      items: pagination,
      processing: [],
      loading: false,
      copying: [],
      info: null
    }
  },
  created() {
    this.filters = Object.assign(this.filters, this.$hyperion.request)
    this.$emit('filtered', this.filters)
    this.getItems()
  },
  methods: {
    approveOrRejectRequest(key, action) {
      let index = _.findIndex(this.items.data, {id: key})
      if (index >= 0) {
        if(!this.items.data[index].ref_number){
          this.$notification.error({
            message: 'Validation failed',
            description: 'ref number required'
          })

          return false
        }
        this.putItem(this.items.data[index], action)
      }
    },
    tableChanged(pagination) {
      this.filters.page = pagination.current
      this.pushState(this.filters)
      this.getItems()
    },
    handleActionSuccess(data) {
      let index = _.findIndex(this.items.data, {id: data.id})
      if (index >= 0) {
        this.items.data.splice(index, 1, data)
        this.$message.success("Request #" + data.id + " has been " + data.status.toUpperCase())
        this.items.data = [...this.items.data]
      }
    },
    putItem(param, action) {
      this.processing.push(param.id)
      this.$http.put(this.routeUri('hyperion.finances.deposit.update', {
        balance_request_uuid: param.id
      }), {action: action, ref_number: param.ref_number})
        .then((response) => this.handleActionSuccess(response.data))
        .catch((error) => this.handleXhrError(error))
        .finally(() => _.pull(this.processing, param.id))
    },
    getItems() {
      this.loading = true
      return this.$http.get(this.baseUri, { params: this.filters})
        .then((response) => this.items = response.data)
        .catch((error) => this.handleXhrError(error))
        .finally(() => this.loading = false)
    },
    filtered(filters) {
      this.filtering = false
      Object.assign(this.filters, filters)
      this.$emit('filtered', this.filters)

      this.pushState(this.filters)
      this.getItems()
    },
    getItemCopy(param) {
      this.copying.push(param.id)

      this.$http.get(this.routeUri("hyperion.finances.deposit.show", {
        balance_request_uuid: param.id
      }), { params: {info: true}})
        .then((response) => this.copyInfo(response.data, response.data.type))
        .catch((error) => this.handleXhrError(error))
        .finally(() => _.pull(this.copying, param.id))
    },
    copyInfo(record, serviceType){
      this.info = "Klien: " + record.client.name + "\n"
      + `Jumlah ${_.capitalize(serviceType)}: ` + this.currency(record.amount, 0) + "\n"
      + "Saldo Sebelum: " + this.currency(record.mutation.balance_before, 0) + "\n"
      + "Saldo Sesudah: " + this.currency(record.mutation.balance_after, 0) + "\n"
      + `Tanggal ${_.capitalize(serviceType)}: ` + this.moment(record.approved_at).format('YYYY-MM-DD HH:mm:ss') + "\n"
      + "Petugas: " + record.performer.name

      this.visible = true
    },
    copy(){
      var copyText = document.getElementById("info")

      copyText.select()

      try {
        document.execCommand('copy');
        setTimeout(this.$message.success('Copied to clipboard'), 1500);
      } catch (err) {
        this.$message.error('Failed Copy to clipboard');
      }
    },
    handleDownload() {
      window.open(this.exportUri, '_blank')
    },
  }
}
</script>
<template>
  <div>
    <a-card size="small" type="inner">
      <div slot="extra">
        <a-button class="ml-2" size="small" type="primary" icon="cloud-download" @click="handleDownload"/>
        <a-button size="small" icon="filter" title="Filter Form" @click="() => filtering = true" />
      </div>
      <a-skeleton active :loading="loading">
        <a-table :columns="columns" :dataSource="items.data" :rowKey="(record) => record.id" :pagination="pagination" @change="tableChanged">

          <h5 slot="datetime" slot-scope="datetime" class="text-center">
            {{ moment(datetime).format('DD/MM/YYYY HH:mm:ss') }}
          </h5>

          <div slot="client" slot-scope="client">
            <h5>
              <a-icon type="idcard" /> <a :href="routeUri('hyperion.client.show', {client_hash: client.hash})">{{ client.name }}</a>
              <br>
              <a-icon type="user" /> <small><code>{{ client.username }}</code></small>
            </h5>
          </div>

          <div slot="type" slot-scope="type" class="text-center" >
            <small><a-tag :color="serviceColors[type]">{{ type.toUpperCase() }}</a-tag></small>
          </div>

          <h5 slot="receiver" slot-scope="bank">
            <a-icon type="contacts" /> <strong>{{ bank.account_holder }}</strong>
            <br>
            <a-icon type="bank" /> <strong>[{{ bank.bank_code }}] {{ bank.bank_name }}</strong>
            <br>
            <a-icon type="number" /> <code>{{ bank.account_number }}</code>
          </h5>

          <h5 slot="sender" slot-scope="record">
            <a-icon type="contacts" /> <strong>{{ record.sender_name.toUpperCase() }}</strong>
            <br>
            <a-icon type="bank" /> <strong>{{ record.sender_bank.toUpperCase() }}</strong>
            <br>
            <a-icon type="number" /> <code>{{ record.sender_account }}</code>
          </h5>

          <h5 slot="amount" slot-scope="amount"><currency :amount="amount" /></h5>

          <template slot="refNumber" slot-scope="ref,record">
            <a-input v-model="record.ref_number" v-if="record.status === 'pending'"/>
            <span class="text-center" v-else><small>{{ ref }}</small></span>
          </template>

          <div slot="status" slot-scope="status" class="text-center">
            <small><a-tag :color="statusColors[status]">{{ status.toUpperCase() }}</a-tag></small>
          </div>

          <div slot="action" slot-scope="updated,record" class="text-center" v-if="! viewOnly">
            <template v-if="record.status==='pending'">
              <a-button-group size="small">
                <a-popconfirm title="Are you sure you want to accept this request？" @confirm="approveOrRejectRequest(record.id, 'approve')">
                  <a-button icon="like" type="primary" :loading="processing.includes(record.id)"></a-button>
                </a-popconfirm>

                <a-popconfirm title="Are you sure you want to reject this request？" @confirm="approveOrRejectRequest(record.id, 'reject')">
                  <a-button icon="dislike" type="danger" :loading="processing.includes(record.id)"></a-button>
                </a-popconfirm>
              </a-button-group>
            </template>

            <template v-else>
              <a-tooltip :title="'By: ' + record.performer.name + ' at ' + record.updated_at">
                <a-button icon="question" size="small"></a-button>
              </a-tooltip>

              <a-tooltip title="Copy to clipboard" v-if="record.status !== 'rejected'">
                <a-button icon="copy" size="small" :loading="copying.includes(record.id)" @click="getItemCopy(record)"></a-button>
              </a-tooltip>
            </template>
          </div>
        </a-table>
      </a-skeleton>
    </a-card>

    <a-drawer v-if="! viewOnly" :width="720" placement="right" :closable="true" :visible="filtering" @close="() => filtering = false">
      <h-deposit-request-filter :filters="filters" @filtered="filtered" />
    </a-drawer>

    <a-modal
        :visible="visible"
        :destroyOnClose="true"
        :footer="null"
        :closable="false">

        <a-input type="textarea" v-model="info" id="info" :rows="10" :readOnly="true" /><br/><br/>

        <a-space>
          <a-button @click="() => this.visible = false" >Close</a-button>
          <a-button type="primary" @click="copy">Copy</a-button>
        </a-space>
    </a-modal>
  </div>
</template>
